import React from 'react'
import { useSearchParams } from 'react-router-dom'

import { Tabs } from '@factorialco/f0-react/dist/experimental'
import { ProcurementTypeItemFragment, WorkflowContainerFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { Form } from 'modules/forms'

interface TabContentProps {
  id: string | undefined
  requestType: ProcurementTypeItemFragment | undefined
  isCustomFormsEnabled: boolean
  isWorkflowsEnabled: boolean
  workflow: WorkflowContainerFragment | undefined
  form: Form | undefined
  setForm: (form: Form) => void
}

const FormTab = React.lazy(() => import('modules/procurement/components/procurementTypes/Form'))
const WorkflowsTab = React.lazy(
  () => import('modules/procurement/components/procurementTypes/Workflows')
)

const TabContent = ({
  id,
  requestType,
  isCustomFormsEnabled,
  isWorkflowsEnabled,
  workflow,
  form,
  setForm,
}: TabContentProps): React.JSX.Element => {
  const { navigation } = useNavigation()
  const [searchParams, setSearchParams] = useSearchParams()

  // The active tab is from the params or 'form' by default
  const activeTab = React.useMemo(() => searchParams.get('tab') || 'form', [searchParams])

  React.useEffect(() => {
    if (activeTab === 'workflows' && !isWorkflowsEnabled) {
      setSearchParams({ tab: 'form' })
    } else if (activeTab === 'form' && !isCustomFormsEnabled) {
      setSearchParams({ tab: 'workflows' })
    }
  }, [activeTab, isWorkflowsEnabled, isCustomFormsEnabled, setSearchParams])

  const Content = React.useMemo<React.ReactNode>(() => {
    const hasContent = requestType && id && (isCustomFormsEnabled || isWorkflowsEnabled)

    if (!hasContent) {
      return null
    }

    switch (activeTab) {
      case 'workflows':
        return isWorkflowsEnabled ? (
          <WorkflowsTab requestType={requestType} workflow={workflow ?? null} />
        ) : null
      case 'form':
      default:
        return isCustomFormsEnabled ? (
          <FormTab requestType={requestType} form={form} setForm={setForm} />
        ) : null
    }
  }, [
    activeTab,
    form,
    setForm,
    isCustomFormsEnabled,
    isWorkflowsEnabled,
    id,
    requestType,
    workflow,
  ])

  return (
    <>
      {/* Tabs */}
      <Tabs
        key={activeTab}
        activeTabId={activeTab}
        tabs={[
          isCustomFormsEnabled && {
            id: 'form',
            href: `${navigation.settings.procurement.requestType.id.fullPath({
              id: id || '',
            })}?tab=form`,
            label: i18n.t('procurement.purchase_requests.settings.types.detail.tabs.form.title'),
            index: true,
          },
          isWorkflowsEnabled && {
            id: 'workflows',
            href: `${navigation.settings.procurement.requestType.id.fullPath({
              id: id || '',
            })}?tab=workflows`,
            label: i18n.t(
              'procurement.purchase_requests.settings.types.detail.tabs.workflows.title'
            ),
          },
        ].filter(Boolean)}
      />

      {Content}
    </>
  )
}

export default React.memo(TabContent)
